import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Coffee, Clock, Mail, ArrowRight } from 'lucide-react';

const SteamAnimation = () => (
  <div className="absolute -top-8 left-1/2 -translate-x-1/2 flex gap-2">
    {[0, 1, 2].map((i) => (
      <motion.div
        key={i}
        className="w-1.5 rounded-full bg-coffee-300/60"
        initial={{ height: 0, opacity: 0, y: 0 }}
        animate={{
          height: [0, 20, 30, 10, 0],
          opacity: [0, 0.7, 0.5, 0.3, 0],
          y: [0, -10, -25, -40, -55],
        }}
        transition={{
          duration: 2.5,
          repeat: Infinity,
          delay: i * 0.4,
          ease: 'easeOut',
        }}
      />
    ))}
  </div>
);

function UnderConstruction() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (email) {
      setSubmitted(true);
      setEmail('');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-espresso via-coffee-900 to-coffee-800 flex items-center justify-center px-4 py-12 overflow-hidden relative">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-10 left-10 w-72 h-72 bg-coffee-700/10 rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-coffee-600/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-coffee-500/5 rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
        className="relative z-10 max-w-2xl w-full text-center"
      >
        {/* Coffee cup icon with steam */}
        <div className="relative inline-block mb-8">
          <SteamAnimation />
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200, delay: 0.3 }}
            className="w-24 h-24 bg-gradient-to-br from-coffee-400 to-coffee-600 rounded-full flex items-center justify-center shadow-2xl shadow-coffee-900/50"
          >
            <Coffee className="w-12 h-12 text-cream" />
          </motion.div>
        </div>

        {/* Main heading */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="font-serif text-5xl md:text-6xl text-cream mb-4 leading-tight"
        >
          Brewing Something
          <span className="block text-coffee-400">Special</span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-coffee-300 text-lg md:text-xl mb-10 max-w-md mx-auto leading-relaxed"
        >
          Our website is currently under construction. We're crafting the perfect
          experience for you — just like our coffee.
        </motion.p>

        {/* Animated divider */}
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: 0.7, duration: 0.6 }}
          className="h-px bg-gradient-to-r from-transparent via-coffee-500 to-transparent mb-10 max-w-xs mx-auto"
        />

        {/* Info cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10"
        >
          <div className="bg-coffee-900/40 backdrop-blur-sm border border-coffee-700/30 rounded-2xl p-6 text-left">
            <Clock className="w-6 h-6 text-coffee-400 mb-3" />
            <h3 className="text-cream font-semibold mb-1">Coming Soon</h3>
            <p className="text-coffee-400 text-sm">
              We're working hard to bring you an amazing online experience.
              Stay tuned!
            </p>
          </div>
          <div className="bg-coffee-900/40 backdrop-blur-sm border border-coffee-700/30 rounded-2xl p-6 text-left">
            <Mail className="w-6 h-6 text-coffee-400 mb-3" />
            <h3 className="text-cream font-semibold mb-1">Get Notified</h3>
            <p className="text-coffee-400 text-sm">
              Leave your email and we'll let you know the moment we're back
              online.
            </p>
          </div>
        </motion.div>

        {/* Email signup */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
        >
          {submitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-coffee-700/30 border border-coffee-600/30 rounded-2xl p-6"
            >
              <p className="text-cream font-medium text-lg">
                Thank you for subscribing!
              </p>
              <p className="text-coffee-400 text-sm mt-1">
                We'll notify you when the site is ready.
              </p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="flex-1 px-5 py-3.5 rounded-xl bg-coffee-900/50 border border-coffee-700/40 text-cream placeholder-coffee-500 focus:outline-none focus:border-coffee-400 focus:ring-1 focus:ring-coffee-400/30 transition-all"
              />
              <button
                type="submit"
                className="px-6 py-3.5 bg-gradient-to-r from-coffee-500 to-coffee-600 hover:from-coffee-400 hover:to-coffee-500 text-white font-semibold rounded-xl transition-all duration-300 flex items-center justify-center gap-2 shadow-lg shadow-coffee-900/30 hover:shadow-coffee-800/40"
              >
                Notify Me
                <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          )}
        </motion.div>

        {/* Footer note */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="text-coffee-600 text-sm mt-12"
        >
          &copy; {currentTime.getFullYear()} Sapo Coffee House. All rights reserved.
        </motion.p>
      </motion.div>
    </div>
  );
}

export default UnderConstruction;
